import { FieldError } from '@automata/ui';
import { useFlag } from '@automata/utils/hooks';
import Stack from '@mui/material/Stack';
import { useCallback } from 'react';

import { ExposingParameterChip } from './components/ExposingParameterChip';
import { ExposingParameterPopover } from './components/ExposingParameterPopover/ExposingParameterPopover';
import { TaskDependenciesTable } from './components/TaskDependenciesTable';
import { TaskMenu } from './components/TaskMenu';
import { ConnectorSelect } from './ConnectorSelect';
import { useFieldData } from './hooks';
import { SidebarWrap } from './SidebarWrap';
import { deselectNodesEdges } from '../../features/graph/graphSlice';
import { selectCommentsById } from '../../features/workflow/selectors';
import {
  setComment,
  updateTaskProperties,
} from '../../features/workflow/workflowSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import {
  DataExportTaskProperties,
  ExportDataTaskPropertiesForm,
  PropertiesType,
} from '../../types';
import Chip from '../ChipWFB';
import { Icon } from '../customIcons/misc';
import { ParameterType } from '../GraphEditorToolbars/ParametersToolbar/types';
import { SemicontrolledInput } from '../SemicontrolledInput';

export interface ExportTaskSidebarProps {
  selectedNodeId: string;
  properties: DataExportTaskProperties;
}

export const FILE_PATH_FIELD = 'file_path';

export const ExportTaskSidebar = ({
  selectedNodeId,
  properties,
}: ExportTaskSidebarProps) => {
  const dispatch = useAppDispatch();
  const comment = useAppSelector(selectCommentsById)[selectedNodeId];
  const workflowLevelErrorsVisible = useFlag(
    'fe.canvas-builder-workflow-errors-visible'
  );
  const useStaticFilePathInputEnabled = useFlag(
    'fe.use_static_file_path_input'
  );

  const { getFieldData } = useFieldData(properties);
  const {
    parameterAppliedReferenceId,
    parameterReference,
    parameterAppliedValue,
  } = getFieldData(FILE_PATH_FIELD);

  const error =
    workflowLevelErrorsVisible && !parameterAppliedValue
      ? 'File path is required.'
      : null;

  const handleUpdateTaskFields = useCallback(
    (formUpdate: Partial<ExportDataTaskPropertiesForm>) => {
      const { nickname } = formUpdate;
      const nicknameUpdate =
        typeof nickname === 'string'
          ? { nickname: nickname.trim() === '' ? '-' : nickname.trim() }
          : {};

      dispatch(
        updateTaskProperties({
          ...formUpdate,
          ...nicknameUpdate,
          id: selectedNodeId,
          propertiesType: PropertiesType.DataExport,
        })
      );
    },
    [selectedNodeId, dispatch]
  );

  const handleCloseSidebar = useCallback(() => {
    dispatch(deselectNodesEdges());
  }, [dispatch]);

  const handleSetComment = useCallback(
    (comment: string) => {
      dispatch(setComment({ id: selectedNodeId, comment }));
    },
    [selectedNodeId, dispatch]
  );

  return (
    <SidebarWrap
      comment={comment}
      handleCloseSidebar={handleCloseSidebar}
      handleSetComment={handleSetComment}
    >
      <Stack gap={2}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          pb={1}
        >
          {/* minHeight is a quick way keep visual consistency between this component and BaseTaskSettings */}
          <Stack direction="row" alignItems="center" gap={2} minHeight={50}>
            <Icon color="neutral" fontSize="large" iconType="processdata" />
            <Chip label="Export Data" />
          </Stack>
          <TaskMenu taskId={properties.id} />
        </Stack>

        <SemicontrolledInput
          TextFieldProps={{ label: 'Task name' }}
          value={properties.nickname ?? ''}
          handleSaveValue={(value) =>
            handleUpdateTaskFields({ nickname: value })
          }
          maxLength={25}
        />

        <ConnectorSelect
          label="Output connector"
          value={properties.connectorType}
          onChange={(value) =>
            handleUpdateTaskFields({ connectorType: value ?? '' })
          }
        />

        {useStaticFilePathInputEnabled && (
          <Stack gap={1}>
            <Stack flexDirection="row" alignItems="flex-end" width="100%">
              <SemicontrolledInput
                TextFieldProps={{
                  disabled: !!parameterAppliedReferenceId,
                  error: !!error,
                  label: 'File path',
                }}
                maxLength={50}
                value={parameterAppliedValue}
                handleSaveValue={(value) =>
                  handleUpdateTaskFields({
                    taskParameters: {
                      [FILE_PATH_FIELD]: value,
                    },
                  })
                }
              />
              <ExposingParameterPopover
                appliedParameterId={parameterReference?.id}
                parameterName={FILE_PATH_FIELD}
                taskId={properties.id}
                types={ParameterType.STRING}
              />
            </Stack>
            {parameterAppliedReferenceId && (
              <ExposingParameterChip
                fieldName={FILE_PATH_FIELD}
                parameter={parameterReference}
                taskId={properties.id}
              />
            )}
            <FieldError error={error} />
          </Stack>
        )}

        <TaskDependenciesTable taskId={properties.id} />
      </Stack>
    </SidebarWrap>
  );
};
